import { useQueries, useQuery } from '@tanstack/react-query';
import {
  pickerSuggestionsContract,
  type PickerSuggestionEntityType,
  type PickerSuggestionsResponse
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { pickerSuggestionsKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';

async function fetchPickerSuggestions(
  filters: {
    entityType: PickerSuggestionEntityType;
    processId?: string;
    recordingId?: string;
  },
  signal: AbortSignal | undefined
): Promise<PickerSuggestionsResponse['data']> {
  const params = new URLSearchParams({ entityType: filters.entityType });
  if (filters.processId) params.set('processId', filters.processId);
  if (filters.recordingId) params.set('recordingId', filters.recordingId);
  const { data } = await apiClient.get<PickerSuggestionsResponse>(
    `${pickerSuggestionsContract.path}?${params.toString()}`,
    { signal }
  );
  return data.data;
}

interface UsePickerSuggestionsArgs {
  entityType: PickerSuggestionEntityType;
  processId?: string;
  recordingId?: string;
  enabled?: boolean;
}

export function usePickerSuggestions({
  entityType,
  processId,
  recordingId,
  enabled = true
}: UsePickerSuggestionsArgs) {
  const { token } = useAuthStore();
  const filters = { entityType, processId, recordingId };

  return useQuery({
    queryKey: pickerSuggestionsKeys.list(filters),
    queryFn: ({ signal }) => fetchPickerSuggestions(filters, signal),
    enabled: !!token && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });
}

interface PerEntityArgs {
  processId?: string;
  recordingId?: string;
  enabled?: boolean;
}

export function useSystemPickerSuggestions(args: PerEntityArgs) {
  return usePickerSuggestions({ entityType: 'system', ...args });
}

export function useUserPickerSuggestions(args: PerEntityArgs) {
  return usePickerSuggestions({ entityType: 'user', ...args });
}

export function useOrgFunctionPickerSuggestions(args: PerEntityArgs) {
  return usePickerSuggestions({ entityType: 'orgFunction', ...args });
}

export function useExternalContactPickerSuggestions(args: PerEntityArgs) {
  return usePickerSuggestions({ entityType: 'externalContact', ...args });
}

export function useArtifactPickerSuggestions(args: PerEntityArgs) {
  return usePickerSuggestions({ entityType: 'artifact', ...args });
}

/**
 * Warm the picker-suggestions cache at parent mount so the first open of a
 * picker doesn't render an empty bucket list mid-animation. The picker's own
 * `enabled: open` fetch then hits the cache immediately.
 */
export function usePrefetchPickerSuggestions(
  entityTypes: PickerSuggestionEntityType[],
  args: Omit<PerEntityArgs, 'enabled'>
) {
  const { token } = useAuthStore();
  useQueries({
    queries: entityTypes.map((entityType) => {
      const filters = { entityType, ...args };
      return {
        queryKey: pickerSuggestionsKeys.list(filters),
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchPickerSuggestions(filters, signal),
        enabled: !!token,
        staleTime: 30_000,
        refetchOnWindowFocus: false
      };
    })
  });
}
